import { GameState, UndoPatch, Bottle } from "./types";

export function createUndoPatch(state: GameState): UndoPatch {
  return {
    bottles: state.bottles.map(b => ({
      id: b.id,
      layers: [...b.layers],
      sealed: b.sealed,
      visibility: b.visibility
    })),
    packingSlots: [...state.packing.slots],
    packingQueue: [...state.packing.queue],
    score: state.score,
    phase: state.phase,
    hiddenBottles: state.hiddenBottles.map(b => ({
      id: b.id,
      layers: [...b.layers],
      sealed: b.sealed,
      visibility: b.visibility
    }))
  };
}

export function applyUndoPatch(state: GameState, patch: UndoPatch): void {
  // Restore hidden bottles if present
  if (patch.hiddenBottles) {
    state.hiddenBottles = patch.hiddenBottles.map(b => ({
      id: b.id,
      layers: [...b.layers],
      sealed: b.sealed,
      visibility: b.visibility
    }));
  }
  
  state.bottles = patch.bottles.map(b => ({
    id: b.id,
    layers: [...b.layers],
    sealed: b.sealed,
    visibility: b.visibility
  }));
  state.packing.slots = [...patch.packingSlots];
  state.packing.queue = [...patch.packingQueue];
  state.score = patch.score;
  state.phase = patch.phase;
}

export function deepCloneBottle(bottle: Bottle): Bottle {
  return {
    id: bottle.id,
    layers: [...bottle.layers],
    sealed: bottle.sealed,
    visibility: bottle.visibility
  };
}

export function deepCloneGameState(state: GameState): GameState {
  return {
    level: state.level,
    seed: state.seed,
    bottles: state.bottles.map(deepCloneBottle),
    hiddenBottles: state.hiddenBottles.map(deepCloneBottle),
    packing: {
      slots: [...state.packing.slots],
      queue: [...state.packing.queue]
    },
    score: state.score,
    moves: [],
    phase: state.phase,
    initialVisibleCount: state.initialVisibleCount
  };
}

export function areBottlesEqual(a: Bottle, b: Bottle): boolean {
  return (
    a.id === b.id &&
    a.sealed === b.sealed &&
    a.visibility === b.visibility &&
    a.layers.length === b.layers.length &&
    a.layers.every((layer, i) => layer === b.layers[i])
  );
}

export function areUndoPatchesEqual(a: UndoPatch, b: UndoPatch): boolean {
  if (
    a.score !== b.score ||
    a.phase !== b.phase ||
    a.packingSlots.length !== b.packingSlots.length ||
    a.packingQueue.length !== b.packingQueue.length ||
    a.bottles.length !== b.bottles.length
  ) {
    return false;
  }

  for (let i = 0; i < a.packingSlots.length; i++) {
    if (a.packingSlots[i] !== b.packingSlots[i]) return false;
  }

  for (let i = 0; i < a.packingQueue.length; i++) {
    if (a.packingQueue[i] !== b.packingQueue[i]) return false;
  }

  for (let i = 0; i < a.bottles.length; i++) {
    if (!areBottlesEqual(a.bottles[i], b.bottles[i])) return false;
  }
  
  // Compare hidden bottles if present
  const aHidden = a.hiddenBottles ?? [];
  const bHidden = b.hiddenBottles ?? [];
  if (aHidden.length !== bHidden.length) return false;
  for (let i = 0; i < aHidden.length; i++) {
    if (!areBottlesEqual(aHidden[i], bHidden[i])) return false;
  }

  return true;
}
